import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { MousePointerClick, Users, Euro, Percent, TrendingUp, Info } from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend 
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardErgebnisse = () => {
  const [stats, setStats] = useState({
    clicks: 0,
    impressions: 0,
    anfragen: 0,
    kosten: 0,
    ctr: 0,
    conversionRate: 0,
    kostenProAnfrage: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [barChartData, setBarChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTooltips, setOpenTooltips] = useState({});

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch aggregated KPIs
      const kpisResponse = await axios.get(`${API}/dashboard/kpis?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const kpis = kpisResponse.data;

      setStats({
        clicks: kpis.total_clicks || 0,
        impressions: kpis.total_impressions || 0,
        anfragen: kpis.total_conversions || 0,
        kosten: kpis.total_cost || 0,
        ctr: kpis.ctr || 0,
        conversionRate: kpis.conversion_rate || 0,
        kostenProAnfrage: kpis.cost_per_conversion || 0,
      });

      // Format chart data
      const chartArray = (kpis.daily_stats || []).map(day => ({
        date: new Date(day.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
        klicks: day.clicks || 0,
        anfragen: day.conversions || 0,
        kosten: day.cost || 0,
        impressionen: day.impressions || 0
      }));

      setChartData(chartArray);

      // Prepare bar chart data (last 7 days)
      const lastSevenDays = chartArray.slice(-7);
      setBarChartData(lastSevenDays);

    } catch (error) {
      console.error('Error fetching analytics data:', error);
      toast.error('Fehler beim Laden der Analytics-Daten');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0040FF] mx-auto mb-4"></div>
          <p className="text-gray-600">Analytics werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Ergebnisse & Analytics</h1>
        <p className="text-gray-600">
          Detaillierte Performance-Analyse Ihrer Kampagnen
        </p>
      </div>

      {/* Key Metrics Cards */}
      <TooltipProvider delayDuration={300} skipDelayDuration={100}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Klicks */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  Klicks (30 Tage)
                  <UITooltip 
                    delayDuration={0}
                    open={openTooltips.clicks}
                    onOpenChange={(open) => setOpenTooltips(prev => ({ ...prev, clicks: open }))}
                  >
                    <TooltipTrigger asChild>
                      <button 
                        type="button" 
                        className="inline-flex items-center touch-manipulation"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTooltips(prev => ({ ...prev, clicks: !prev.clicks }));
                        }}
                        onMouseEnter={() => setOpenTooltips(prev => ({ ...prev, clicks: true }))}
                        onMouseLeave={() => setOpenTooltips(prev => ({ ...prev, clicks: false }))}
                      >
                        <Info className="w-3.5 h-3.5 text-gray-400 cursor-help hover:text-gray-600 transition-colors" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p className="text-sm">Die Anzahl der Klicks auf Ihre Anzeigen in den letzten 30 Tagen.</p>
                    </TooltipContent>
                  </UITooltip>
                </span>
                <MousePointerClick className="w-4 h-4 text-gray-400" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {stats.clicks.toLocaleString('de-DE')}
              </div>
            </CardContent>
          </Card>

          {/* Anfragen */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  Anfragen (30 Tage)
                  <UITooltip 
                    delayDuration={0}
                    open={openTooltips.anfragen}
                    onOpenChange={(open) => setOpenTooltips(prev => ({ ...prev, anfragen: open }))}
                  >
                    <TooltipTrigger asChild>
                      <button 
                        type="button" 
                        className="inline-flex items-center touch-manipulation"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTooltips(prev => ({ ...prev, anfragen: !prev.anfragen }));
                        }}
                        onMouseEnter={() => setOpenTooltips(prev => ({ ...prev, anfragen: true }))}
                        onMouseLeave={() => setOpenTooltips(prev => ({ ...prev, anfragen: false }))}
                      >
                        <Info className="w-3.5 h-3.5 text-gray-400 cursor-help hover:text-gray-600 transition-colors" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p className="text-sm">Die Anzahl der Conversions bzw. Anfragen, die durch Ihre Anzeigen generiert wurden.</p>
                    </TooltipContent>
                  </UITooltip>
                </span>
                <Users className="w-4 h-4 text-gray-400" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                {stats.anfragen.toLocaleString('de-DE')}
              </div>
            </CardContent>
          </Card>

          {/* Kosten */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  Kosten (30 Tage)
                  <UITooltip 
                    delayDuration={0}
                    open={openTooltips.kosten}
                    onOpenChange={(open) => setOpenTooltips(prev => ({ ...prev, kosten: open }))}
                  >
                    <TooltipTrigger asChild>
                      <button 
                        type="button" 
                        className="inline-flex items-center touch-manipulation"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTooltips(prev => ({ ...prev, kosten: !prev.kosten }));
                        }}
                        onMouseEnter={() => setOpenTooltips(prev => ({ ...prev, kosten: true }))}
                        onMouseLeave={() => setOpenTooltips(prev => ({ ...prev, kosten: false }))}
                      >
                        <Info className="w-3.5 h-3.5 text-gray-400 cursor-help hover:text-gray-600 transition-colors" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p className="text-sm">Die Gesamtkosten Ihrer Anzeigenkampagnen in den letzten 30 Tagen.</p>
                    </TooltipContent>
                  </UITooltip>
                </span>
                <Euro className="w-4 h-4 text-gray-400" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">
                €{stats.kosten.toFixed(2)}
              </div>
            </CardContent>
          </Card>

          {/* CTR */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  Click-Through-Rate
                  <UITooltip 
                    delayDuration={0}
                    open={openTooltips.ctr}
                    onOpenChange={(open) => setOpenTooltips(prev => ({ ...prev, ctr: open }))}
                  >
                    <TooltipTrigger asChild>
                      <button 
                        type="button" 
                        className="inline-flex items-center touch-manipulation"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTooltips(prev => ({ ...prev, ctr: !prev.ctr }));
                        }}
                        onMouseEnter={() => setOpenTooltips(prev => ({ ...prev, ctr: true }))}
                        onMouseLeave={() => setOpenTooltips(prev => ({ ...prev, ctr: false }))}
                      >
                        <Info className="w-3.5 h-3.5 text-gray-400 cursor-help hover:text-gray-600 transition-colors" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p className="text-sm">Der Prozentsatz der Impressionen, die zu einem Klick führten. Berechnet als (Klicks / Impressionen) × 100.</p>
                    </TooltipContent>
                  </UITooltip>
                </span>
                <Percent className="w-4 h-4 text-gray-400" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {stats.ctr.toFixed(2)}%
              </div>
              <p className="text-sm text-gray-500">
                {stats.clicks.toLocaleString('de-DE')} / {stats.impressions.toLocaleString('de-DE')} Impressionen
              </p>
            </CardContent>
          </Card>

          {/* Conversion Rate */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  Conversion-Rate
                  <UITooltip 
                    delayDuration={0}
                    open={openTooltips.conversionRate}
                    onOpenChange={(open) => setOpenTooltips(prev => ({ ...prev, conversionRate: open }))}
                  >
                    <TooltipTrigger asChild>
                      <button 
                        type="button" 
                        className="inline-flex items-center touch-manipulation"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTooltips(prev => ({ ...prev, conversionRate: !prev.conversionRate }));
                        }}
                        onMouseEnter={() => setOpenTooltips(prev => ({ ...prev, conversionRate: true }))}
                        onMouseLeave={() => setOpenTooltips(prev => ({ ...prev, conversionRate: false }))}
                      >
                        <Info className="w-3.5 h-3.5 text-gray-400 cursor-help hover:text-gray-600 transition-colors" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p className="text-sm">Der Prozentsatz der Klicks, die zu einer Anfrage führten. Berechnet als (Anfragen / Klicks) × 100.</p>
                    </TooltipContent>
                  </UITooltip>
                </span>
                <TrendingUp className="w-4 h-4 text-gray-400" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {stats.conversionRate.toFixed(2)}%
              </div>
              <p className="text-sm text-gray-500">
                {stats.anfragen} / {stats.clicks.toLocaleString('de-DE')} Klicks
              </p>
            </CardContent>
          </Card>

          {/* Kosten pro Anfrage */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  Kosten pro Anfrage
                  <UITooltip 
                    delayDuration={0}
                    open={openTooltips.kostenProAnfrage}
                    onOpenChange={(open) => setOpenTooltips(prev => ({ ...prev, kostenProAnfrage: open }))}
                  >
                    <TooltipTrigger asChild>
                      <button 
                        type="button" 
                        className="inline-flex items-center touch-manipulation"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTooltips(prev => ({ ...prev, kostenProAnfrage: !prev.kostenProAnfrage }));
                        }}
                        onMouseEnter={() => setOpenTooltips(prev => ({ ...prev, kostenProAnfrage: true }))}
                        onMouseLeave={() => setOpenTooltips(prev => ({ ...prev, kostenProAnfrage: false }))}
                      >
                        <Info className="w-3.5 h-3.5 text-gray-400 cursor-help hover:text-gray-600 transition-colors" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p className="text-sm">Durchschnittliche Kosten pro gewonnener Anfrage. Berechnet als (Gesamtkosten / Anzahl Anfragen).</p>
                    </TooltipContent>
                  </UITooltip>
                </span>
                <Euro className="w-4 h-4 text-gray-400" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                €{stats.kostenProAnfrage.toFixed(2)}
              </div>
              <p className="text-sm text-gray-500">
                Durchschnittliche Akquisitionskosten
              </p>
            </CardContent>
          </Card>
        </div>
      </TooltipProvider>

      {/* Main Performance Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Entwicklung</CardTitle>
          <CardDescription>Klicks, Anfragen und Kosten der letzten 30 Tage</CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#888888"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#888888"
                  fontSize={12}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="klicks" 
                  stroke="#0040FF" 
                  strokeWidth={3}
                  name="Klicks"
                  dot={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="anfragen" 
                  stroke="#10B981" 
                  strokeWidth={3}
                  name="Anfragen"
                  dot={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="kosten" 
                  stroke="#F59E0B" 
                  strokeWidth={3}
                  name="Kosten (€)"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[400px] flex items-center justify-center text-gray-500">
              Keine Daten für die letzten 30 Tage verfügbar
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bar Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Klicks letzte 7 Tage */}
        <Card>
          <CardHeader>
            <CardTitle>Klicks (letzte 7 Tage)</CardTitle>
          </CardHeader>
          <CardContent>
            {barChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={barChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="klicks" fill="#0040FF" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">
                Keine Daten verfügbar
              </div>
            )}
          </CardContent>
        </Card>

        {/* Anfragen letzte 7 Tage */}
        <Card>
          <CardHeader>
            <CardTitle>Anfragen (letzte 7 Tage)</CardTitle>
          </CardHeader>
          <CardContent>
            {barChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={barChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="anfragen" fill="#10B981" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">
                Keine Daten verfügbar
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardErgebnisse;
